/**
 * Turns one "Before you submit" panel commit (`onEditField(entry, newValue)`) into the list of
 * per-field edits to report as `broker_verification_field_edited` events. Pure -- the caller
 * passes a snapshot of the relevant Redux slices, taken BEFORE the edit is dispatched.
 */

const FIELD_LABELS = {
  'commercial_property.address': 'Address',
  'commercial_property.city': 'City',
  'commercial_property.state_abbreviation': 'State',
  'commercial_property.postal_code': 'Zip',
  'commercial_property.edp_property_id': 'EDP Property ID',
  'comp_data.rent_escalation_is_percent': 'Escalation Type ($ or %)',
  'comp_data.rent_escalation_percent': 'Escalation (Percent)',
  'comp_data.rent_escalation_dollar': 'Escalation (Dollar)',
  'deal_contact.given_name': 'First Name',
  'deal_contact.surname': 'Last Name',
  'deal_contact.phone': 'Phone #',
  'deal_contact.email': 'Email Address',
  'deal_contact.job_title': 'Job Title',
  'deal.account_code': 'Client Code',
  'deal.account_description': 'Client Description',
};

const humanize = key => key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

const toBoolean = (value) => {
  if (value === true || value === 'true' || value === 'Yes') return true;
  if (value === false || value === 'false' || value === 'No') return false;
  return null;
};

// Entity-level info per `editTarget.reduxAction`: which record the edited field lives on.
const getEntityInfo = (reduxAction, targetId, state) => {
  switch (reduxAction) {
    case 'updateDeal':
      return { entityType: 'deal', entity: state.deal, entityId: state.deal?.id };
    case 'updateVoucher':
      return { entityType: 'voucher', entity: state.voucher, entityId: state.voucher?.id };
    case 'updateCommissionAttachments':
      return { entityType: 'deal', entity: null, entityId: state.deal?.id };
    case 'updateCompData':
      return { entityType: 'comp_data', entity: state.compData, entityId: state.compData?.id };
    case 'updateCommercialProperty':
      return {
        entityType: 'commercial_property',
        entity: (state.commercialProperties || []).find(property => property.id === targetId),
        entityId: targetId,
      };
    case 'updateSpace':
      return { entityType: 'commercial_property_space', entity: null, entityId: targetId };
    case 'updateCompany':
      return { entityType: 'company', entity: null, entityId: targetId };
    case 'updateDealContact':
      return {
        entityType: 'deal_contact',
        entity: (state.contacts || []).find(contact => contact?.id === targetId),
        entityId: targetId,
      };
    default:
      return null;
  }
};

// Phone/email live inside arrays on the contact record; every other field is flat.
const getContactFieldValue = (contact, key) => {
  if (!contact) return undefined;
  if (key === 'phone') return contact.phone_numbers?.[0]?.number;
  if (key === 'email') return contact.email_addresses?.[0]?.address;
  return contact[key];
};

// Mirrors the guards in ReviewTab.handleEditField that make it a silent no-op (nothing is saved),
// so those commits are not reported as edits either.
const canBeSaved = ({
  reduxAction, targetId, spaceIndex, dealFieldPath,
}, state) => {
  switch (reduxAction) {
    case 'updateCompData':
      return !!state.compData?.id;
    case 'updateCommercialProperty':
    case 'updateDealContact':
      return !!targetId;
    case 'updateSpace': {
      const property = (state.commercialProperties || []).find(candidate => candidate.id === targetId);
      return (property?.spaces?.length || 0) > spaceIndex;
    }
    case 'updateCompany':
      // With no company yet, one is created -- but only when the deal and party field are known.
      return !!targetId || !!(state.deal?.id && dealFieldPath);
    case 'updateCommissionAttachments':
      return !!state.deal?.id;
    default:
      return true;
  }
};

const buildEditEvents = (entry, newValue, state) => {
  const {
    reduxAction, fieldPath, targetId, spaceIndex,
  } = entry.editTarget || {};
  const info = getEntityInfo(reduxAction, targetId, state);
  if (!info || !fieldPath) return [];
  if (!canBeSaved(entry.editTarget, state)) return [];

  const { entityType, entity, entityId } = info;
  const partyType = (/^company-contact-([a-z]+)-/.exec(entry.fieldId || '') || [])[1];
  const common = {
    section: entry.section,
    entityType,
    entityId,
    spaceIndex: reduxAction === 'updateSpace' ? spaceIndex : undefined,
    partyType,
  };
  const event = (fieldKey, fieldLabel, seedPrevious, currentValue, extra = {}) => ({
    ...common, fieldKey, fieldLabel, seedPrevious, currentValue, ...extra,
  });
  const keyFor = key => `${entityType}.${key}`;
  const labelFor = key => FIELD_LABELS[keyFor(key)] || (key === fieldPath ? entry.label : humanize(key));

  // One commit that writes several real fields (address lookup, contact pick, escalation
  // type/amount): one event per field, each compared against the record's current value.
  const fanOut = (payload, readPrevious) => Object.keys(payload)
    .filter(key => !Array.isArray(payload[key]) && !isPlainObject(payload[key]))
    .map(key => event(keyFor(key), labelFor(key), readPrevious(key), payload[key], key === fieldPath
      ? { bounds: entry.bounds, options: entry.options }
      : {}));

  if (reduxAction === 'updateDeal' && fieldPath === 'account_code_description' && isPlainObject(newValue)) {
    return [
      event('deal.account_code', FIELD_LABELS['deal.account_code'], entity?.account_code, newValue.code),
      event('deal.account_description', FIELD_LABELS['deal.account_description'], entity?.account_description, newValue.description),
    ];
  }

  if (isPlainObject(newValue)) {
    if (reduxAction === 'updateDeal') {
      // Company Name search commits the selected/created company record.
      return [event(keyFor(fieldPath), entry.label, entry.value, newValue.name ?? null)];
    }
    if (reduxAction === 'updateCompany') {
      // Business Type / Use ID commits {voucher_naics_code, voucher_naics_category}.
      return [event(keyFor(fieldPath), entry.label, entry.value, newValue.voucher_naics_category ?? null)];
    }
    if (reduxAction === 'updateDealContact') {
      return fanOut(
        {
          given_name: newValue.given_name,
          surname: newValue.surname,
          phone: newValue.phone_numbers?.[0]?.number,
          email: newValue.email_addresses?.[0]?.address,
          job_title: newValue.job_title,
        },
        key => getContactFieldValue(entity, key),
      );
    }
    return fanOut(newValue, key => entity?.[key]);
  }

  const seed = entry.controlType === 'boolean' ? toBoolean(entry.value) : entry.value;
  return [event(keyFor(fieldPath), entry.label, seed, newValue, { bounds: entry.bounds, options: entry.options })];
};

export { buildEditEvents, humanize };
